import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '@/services/api';
import { scheduleReleaseUiScrollLock } from '@/utils/releaseUiScrollLock';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const USER_ROLES = {
  ADMIN: 'admin',
  STORE_OWNER: 'store_owner',
  CUSTOMER: 'customer'
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('byggfynd_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await authApi.getMe();
          setUser(response.data.user);
          scheduleReleaseUiScrollLock();
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('byggfynd_token');
          setToken(null);
          scheduleReleaseUiScrollLock();
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  useEffect(() => {
    const onForcedLogout = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener('auth:logout', onForcedLogout);
    return () => window.removeEventListener('auth:logout', onForcedLogout);
  }, []);

  const login = async (email, password) => {
    const response = await authApi.login(email, password);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('byggfynd_token', newToken);
    setToken(newToken);
    setUser(userData);
    scheduleReleaseUiScrollLock();
    return userData;
  };

  const register = async (data) => {
    const response = await authApi.register(data);
    const { token: newToken, user: userData, message } = response.data;
    if (newToken) {
      localStorage.setItem('byggfynd_token', newToken);
      setToken(newToken);
      setUser(userData);
    }
    return { user: userData, message };
  };

  const registerStore = async (userData, storeData) => {
    const response = await authApi.registerStore(
      {
        email: userData.email,
        password: userData.password,
        name: userData.name || storeData.name,
        company_name: userData.company_name || storeData.name,
        accept_terms: Boolean(userData.accept_terms),
      },
      storeData
    );
    const { token: newToken, user: newUser, store } = response.data;
    if (newToken) {
      localStorage.setItem('byggfynd_token', newToken);
      setToken(newToken);
      setUser(newUser);
      scheduleReleaseUiScrollLock();
    }
    return { user: newUser, store, message: response.data.message };
  };

  // Function to manually set auth data (used after external registration)
  const setAuthData = (newToken, userData) => {
    localStorage.setItem('byggfynd_token', newToken);
    setToken(newToken);
    setUser(userData);
    scheduleReleaseUiScrollLock();
  };

  const logout = () => {
    localStorage.removeItem('byggfynd_token');
    setToken(null);
    setUser(null);
    scheduleReleaseUiScrollLock();
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    registerStore,
    setAuthData,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || user?.role === USER_ROLES.ADMIN,
    isStoreOwner: user?.role === USER_ROLES.STORE_OWNER,
    isCustomer: user?.role === USER_ROLES.CUSTOMER || !user?.role,
    isBusiness: user?.account_type === 'business',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
